import { Skeleton, SkeletonText } from "@/components/ui";
import { cn } from "@/lib/utils";

/**
 * The three shapes almost every screen in this product is waiting for.
 *
 * They exist so replacing a spinner is a one-line change rather than a reason
 * to leave the spinner in. Each takes the geometry of the real thing — same
 * radii, same padding, same column counts — so the page does not jump when the
 * data lands, and line widths vary so a stack never reads as a table.
 */

/** A grid of cards: campaigns, collaborations, anything in `sm:grid-cols-2 lg:grid-cols-3`. */
export function CardGridSkeleton({ count = 6, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("grid gap-5 sm:grid-cols-2 lg:grid-cols-3", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-card border border-neutral-200 bg-white px-5 py-[18px]">
          <div className="flex items-start justify-between gap-3">
            <Skeleton className="h-4 w-[58%] rounded-sm" />
            <Skeleton className="h-5 w-16 rounded-pill" />
          </div>
          <Skeleton className="mt-2 h-3 w-[40%] rounded-sm" />
          <SkeletonText className="mt-4" lines={2} widths={[86, 52]} />
          <div className="mt-4 flex items-center justify-between border-t border-neutral-100 pt-3">
            <Skeleton className="h-3 w-20 rounded-sm" />
            <Skeleton className="h-5 w-24 rounded-sm" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A stack of rows: the collaboration inbox, applications, opportunities. */
export function ListSkeleton({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("space-y-3", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex items-start gap-4 rounded-card border border-neutral-200 bg-white px-5 py-[18px]"
        >
          <Skeleton className="size-11 shrink-0 rounded-avatar-square" />
          <div className="min-w-0 flex-1">
            <Skeleton className="h-4 w-[44%] rounded-sm" />
            <Skeleton className="mt-2 h-3 w-[72%] rounded-sm" />
            <div className="mt-3 flex gap-2">
              <Skeleton className="h-5 w-20 rounded-pill" />
              <Skeleton className="h-5 w-16 rounded-pill" />
            </div>
          </div>
          <Skeleton className="h-6 w-24 shrink-0 rounded-sm" />
        </div>
      ))}
    </div>
  );
}

/** One detail page: a header card plus a body. */
export function DetailSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-4", className)}>
      <div className="rounded-card border border-neutral-200 bg-white px-5 py-[18px]">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <Skeleton className="h-6 w-[46%] rounded-sm" />
            <Skeleton className="mt-2 h-3 w-[28%] rounded-sm" />
          </div>
          <Skeleton className="h-5 w-20 rounded-pill" />
        </div>
        <div className="mt-5 grid gap-4 border-t border-neutral-100 pt-5 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i}>
              <Skeleton className="h-2.5 w-16 rounded-sm" />
              <Skeleton className="mt-2 h-4 w-24 rounded-sm" />
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-card border border-neutral-200 bg-white px-5 py-[18px]">
        <SkeletonText lines={5} widths={[92, 74, 88, 51, 66]} />
      </div>
    </div>
  );
}
